/**
 * staff-lib.ts — Phần THUẦN của màn một nhân viên AI (`/ai/nhan-vien/:botId`): các mục + nhãn "Cần phát hành" /
 * "Hiệu lực ngay", mục mở từ hash / lý do trạng thái, kỹ năng theo `botIds`, kênh phụ trách, bật/tắt gộp trực tuyến,
 * từ khoá chuyển người, ngưỡng tra tri thức.
 */
import type { AiBot, OnlineStatus } from '@/hooks/use-ai'
import type { StatusSection } from '@/components/ai/bot-status-lib'
import { i18n } from '@/lib/i18n'

// ── Các mục ─────────────────────────────────────────────────────────

export type StaffSection = 'vai-tro' | 'bo-nao' | 'kien-thuc' | 'ky-nang' | 'chuyen-nguoi' | 'gio-lam' | 'nang-cao'

/** 'publish' = thay đổi vào bản nháp, khách dùng sau khi Phát hành; 'live' = có hiệu lực ngay. */
export type SectionScope = 'publish' | 'live'

export interface StaffSectionDef {
  id: StaffSection
  label: string
  scope: SectionScope
  /** Chỉ có ý nghĩa với nhân viên AI tích hợp sẵn (bên thứ ba / webhook tự lo câu trả lời). */
  builtinOnly: boolean
}

export const STAFF_SECTIONS: StaffSectionDef[] = [
  { id: 'vai-tro', get label() { return i18n.t('aiStaff:lib.sections.vai-tro') }, scope: 'publish', builtinOnly: true },
  { id: 'bo-nao', get label() { return i18n.t('aiStaff:lib.sections.bo-nao') }, scope: 'publish', builtinOnly: false },
  { id: 'kien-thuc', get label() { return i18n.t('aiStaff:lib.sections.kien-thuc') }, scope: 'publish', builtinOnly: true },
  { id: 'ky-nang', get label() { return i18n.t('aiStaff:lib.sections.ky-nang') }, scope: 'publish', builtinOnly: true },
  { id: 'chuyen-nguoi', get label() { return i18n.t('aiStaff:lib.sections.chuyen-nguoi') }, scope: 'publish', builtinOnly: true },
  { id: 'gio-lam', get label() { return i18n.t('aiStaff:lib.sections.gio-lam') }, scope: 'live', builtinOnly: false },
  { id: 'nang-cao', get label() { return i18n.t('aiStaff:lib.sections.nang-cao') }, scope: 'publish', builtinOnly: true },
]

export const SCOPE_LABEL: Record<SectionScope, string> = {
  get publish() { return i18n.t('aiStaff:lib.scope.publish') },
  get live() { return i18n.t('aiStaff:lib.scope.live') },
}
export const SCOPE_HINT: Record<SectionScope, string> = {
  get publish() { return i18n.t('aiStaff:lib.scopeHint.publish') },
  get live() { return i18n.t('aiStaff:lib.scopeHint.live') },
}

export function sectionScope(id: StaffSection): SectionScope {
  return STAFF_SECTIONS.find((s) => s.id === id)?.scope ?? 'publish'
}

/** Các mục hiện cho một loại nhân viên AI. */
export function sectionsFor(kind: AiBot['kind']): StaffSectionDef[] {
  return kind === 'builtin' ? STAFF_SECTIONS : STAFF_SECTIONS.filter((s) => !s.builtinOnly)
}

/** `#bo-nao` → 'bo-nao'; hash lạ / mục không có với loại bot → mục đầu. Nhận cả mục của máy chủ (`#kenh`…). */
export function sectionFromHash(hash: string | null | undefined, kind: AiBot['kind'] = 'builtin'): StaffSection {
  const h = (hash ?? '').replace(/^#/, '')
  const list = sectionsFor(kind)
  const mapped = (h in STATUS_SECTION_TAB ? STATUS_SECTION_TAB[h as Exclude<StatusSection, 'phat-hanh'>] : h) as StaffSection
  return list.some((s) => s.id === mapped) ? mapped : list[0].id
}

/** Mục máy chủ (`fix.section`) → mục của màn. */
export const STATUS_SECTION_TAB: Record<Exclude<StatusSection, 'phat-hanh'>, StaffSection> = {
  'van-hanh': 'gio-lam',
  kenh: 'gio-lam',
  'gio-lam': 'gio-lam',
  'bo-nao': 'bo-nao',
}

/** Khối cần cuộn tới trong mục "Giờ làm & kênh" theo lý do (để mắt người dùng rơi đúng chỗ). */
export const STATUS_SECTION_ANCHOR: Record<Exclude<StatusSection, 'phat-hanh'>, string> = {
  'van-hanh': 'staff-van-hanh',
  kenh: 'staff-kenh',
  'gio-lam': 'staff-gio-lam',
  'bo-nao': 'staff-bo-nao',
}

// ── Bật / tắt (gộp Trực tuyến) ──────────────────────────────────────

/**
 * Công tắc "Bật nhân viên AI" gộp với trực tuyến: tắt = `enabled=false` + ngoại tuyến; bật = `enabled=true` và nếu đang
 * ngoại tuyến thì về trực tuyến (giữ "Bận" nếu đang bận — tương thích onlineStatus cũ).
 */
export function enabledPatch(on: boolean, current: OnlineStatus | string | null | undefined): { enabled: boolean; onlineStatus: OnlineStatus } {
  if (!on) return { enabled: false, onlineStatus: 'offline' }
  return { enabled: true, onlineStatus: current === 'busy' ? 'busy' : 'online' }
}

/** Đang bật thật (bật + không ngoại tuyến). */
export const isOn = (b: { enabled: boolean; onlineStatus?: string | null }) => b.enabled && b.onlineStatus !== 'offline'

// ── Kỹ năng theo botIds ─────────────────────────────────────────────

/** Kỹ năng có áp cho nhân viên AI này không: null/undefined = mọi nhân viên AI. */
export function skillAppliesTo(botIds: string[] | null | undefined, botId: string): boolean {
  return botIds == null || botIds.includes(botId)
}

/**
 * Tick / bỏ tick một kỹ năng cho nhân viên AI này → `botIds` mới.
 * Đang "mọi nhân viên AI" mà bỏ tick → mọi nhân viên AI KHÁC (giữ nguyên cho người khác).
 */
export function toggleSkillBot(botIds: string[] | null | undefined, botId: string, on: boolean, allBotIds: string[]): string[] | null {
  if (botIds == null) return on ? null : allBotIds.filter((id) => id !== botId)
  return on ? (botIds.includes(botId) ? botIds : [...botIds, botId]) : botIds.filter((id) => id !== botId)
}

/** Chia kỹ năng: đang áp cho nhân viên này / còn lại trong Thư mục dùng chung. */
export function splitSkills<T extends { botIds?: string[] | null }>(skills: T[], botId: string): { applied: T[]; available: T[] } {
  return {
    applied: skills.filter((s) => skillAppliesTo(s.botIds, botId)),
    available: skills.filter((s) => !skillAppliesTo(s.botIds, botId)),
  }
}

/** "Mọi nhân viên AI" / "Chỉ nhân viên này" / "Nhân viên này + 2 nhân viên khác" / "Lan, Mai". */
export function skillScopeText(botIds: string[] | null | undefined, botId: string, bots: Array<Pick<AiBot, 'id' | 'name'>>): string {
  if (botIds == null) return i18n.t('aiStaff:lib.skillScope.all')
  const live = botIds.filter((id) => bots.some((b) => b.id === id) || id === botId)
  const others = live.filter((id) => id !== botId)
  if (live.includes(botId)) return others.length ? i18n.t('aiStaff:lib.skillScope.thisPlus', { count: others.length }) : i18n.t('aiStaff:lib.skillScope.onlyThis')
  if (!others.length) return i18n.t('aiStaff:lib.skillScope.none')
  return others.map((id) => bots.find((b) => b.id === id)?.name ?? '').filter(Boolean).join(', ')
}

// ── Từ khoá chuyển người ────────────────────────────────────────────

export const HANDOFF_KEYWORD_MAX = 50
export const HANDOFF_KEYWORD_LEN = 200

/** Lỗi một từ khoá (null = hợp lệ). "/mẫu/cờ" là biểu thức chính quy — khớp backend `compileHandoffKeyword`. */
export function handoffKeywordProblem(k: string): string | null {
  const t = k.trim()
  if (!t) return i18n.t('aiStaff:lib.keyword.empty')
  if (t.length > HANDOFF_KEYWORD_LEN) return i18n.t('aiStaff:lib.keyword.tooLong', { max: HANDOFF_KEYWORD_LEN })
  const m = t.match(/^\/(.+)\/([gimsuy]*)$/)
  if (m) {
    try { new RegExp(m[1], m[2] || 'i') } catch { return i18n.t('aiStaff:lib.keyword.badRegex', { expr: t.slice(0, 40) }) }
  }
  return null
}

/** Thêm từ khoá (bỏ trùng, cắt khoảng trắng, tối đa 50). Trả lỗi nếu không thêm được. */
export function addHandoffKeyword(list: string[], raw: string): { list: string[]; error: string | null } {
  const t = raw.trim()
  if (!t) return { list, error: null }
  const err = handoffKeywordProblem(t)
  if (err) return { list, error: err }
  if (list.includes(t)) return { list, error: null }
  if (list.length >= HANDOFF_KEYWORD_MAX) return { list, error: i18n.t('aiStaff:lib.keyword.maxCount', { max: HANDOFF_KEYWORD_MAX }) }
  return { list: [...list, t], error: null }
}

export const isRegexKeyword = (k: string) => /^\/(.+)\/([gimsuy]*)$/.test(k.trim())

// ── Ngưỡng tra tri thức ─────────────────────────────────────────────

/** Mặc định của máy chủ khi `retrievalThreshold` = null. */
export const DEFAULT_RETRIEVAL_THRESHOLD = 0.5

export const RETRIEVAL_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0.3, get label() { return i18n.t('aiStaff:lib.retrieval.wide') } },
  { value: 0.4, get label() { return i18n.t('aiStaff:lib.retrieval.slightlyWide') } },
  { value: 0.5, get label() { return i18n.t('aiStaff:lib.retrieval.balanced') } },
  { value: 0.6, get label() { return i18n.t('aiStaff:lib.retrieval.slightlyStrict') } },
  { value: 0.7, get label() { return i18n.t('aiStaff:lib.retrieval.strict') } },
]

/** Giá trị hiển thị của ô ngưỡng: null → 0,5 (số thật máy chủ dùng) thay vì "theo cấu hình chung". */
export function retrievalValue(t: number | null | undefined): number {
  return typeof t === 'number' && Number.isFinite(t) ? t : DEFAULT_RETRIEVAL_THRESHOLD
}
